/**
 * File: useInvoiceSendingMutations.ts
 * Path: apps/admin/src/shared/hooks/useInvoiceSendingMutations.ts
 * Description: Sending an invoice, credit note or statement (email,
 * WhatsApp, a logged download), a PDF exactly as it was sent, Liam's tap on
 * a reminder, and Ready to send's one tap (issue the draft, then email it).
 * Afterwards the send log, the reminders and the invoices are read again;
 * any PDF that comes back is downloaded, except a statement preview.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ACCOUNTING_COLLECTIONS } from "@idk/config";
import { toast } from "@idk/ui";
import { base64ToPdfBlob } from "../services/serverDocumentService";
import { invoiceBooks } from "../services/invoiceBooksService";
import { invoiceSending, type InvoiceSendingAction, type InvoiceSendingResult } from "../services/invoiceSendingService";
import { downloadBlob } from "../utils/leadExportUtils";
import { errorMessage } from "./useContractsMutations";
import { contractsKey } from "./useContractsQueries";
import { invoicesKey } from "./useInvoicesQueries";
import { creditNotesKey } from "./useInvoiceLedgerQueries";

export const sendsKey = ["invoiceSends"] as const;
export const remindersKey = [...sendsKey, "reminders"] as const;

const FAILED = "That didn't go. Check your connection and try again.";

export const useInvoiceSendingMutations = () => {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: sendsKey });
    void queryClient.invalidateQueries({ queryKey: invoicesKey });
  };
  const run = useMutation({
    mutationFn: invoiceSending,
    onSuccess: (result, input) => {
      const preview = input.action === "statementPdf" || input.action === "reminders";
      if (!preview && result.base64 && result.filename) downloadBlob(base64ToPdfBlob(result.base64), result.filename);
      if (input.action === "sentPdf" || preview) return;
      refresh();
      if (result.message) toast.success(result.message);
    },
    onError: (error) => toast.error(errorMessage(error, FAILED)),
  });

  /** Ready to send: give the draft its number, then email it. If the email can't go, the invoice is still issued. */
  const issueAndEmail = useMutation({
    mutationFn: async (invoiceId: string) => {
      const issued = await invoiceBooks({ action: "issueDraft", invoiceId });
      try {
        const sent = await invoiceSending({ action: "email", target: { document: "invoice", invoiceId } });
        return { ok: true as const, message: sent.message };
      } catch (error) {
        return { ok: false as const, message: `${issued.number ?? "The invoice"} is issued, but it wasn't emailed: ${errorMessage(error, FAILED)}` };
      }
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: [ACCOUNTING_COLLECTIONS.profiles] });
      void queryClient.invalidateQueries({ queryKey: creditNotesKey });
      void queryClient.invalidateQueries({ queryKey: contractsKey });
      refresh();
      if (result.ok) toast.success(result.message);
      else toast.error(result.message);
    },
    onError: (error) => toast.error(errorMessage(error, FAILED)),
  });

  return {
    run: (input: InvoiceSendingAction, onDone?: (result: InvoiceSendingResult) => void) => run.mutate(input, { onSuccess: (r) => onDone?.(r) }),
    issueAndEmail: (invoiceId: string) => issueAndEmail.mutate(invoiceId),
    pending: run.isPending || issueAndEmail.isPending,
  };
};
